/**
 * Draw an image into a row-count–based grid and return ImageData for slat extraction.
 */

/**
 * @param {HTMLImageElement} img - must be decoded; naturalWidth/Height > 0
 * @param {number} gridWidth
 * @param {number} gridHeight
 * @param {object} [options]
 * @param {boolean} [options.smoothing=false]
 * @returns {ImageData}
 */
export function drawImageContainedToGrid(img, gridWidth, gridHeight, options = {}) {
  const smoothing = options.smoothing ?? false;
  const canvas = document.createElement('canvas');
  canvas.width = gridWidth;
  canvas.height = gridHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2D context unavailable');
  }
  ctx.imageSmoothingEnabled = smoothing;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, gridWidth, gridHeight);

  const iw = img.naturalWidth;
  const ih = img.naturalHeight;
  if (iw <= 0 || ih <= 0) {
    return ctx.getImageData(0, 0, gridWidth, gridHeight);
  }

  const scale = Math.min(gridWidth / iw, gridHeight / ih);
  const dw = iw * scale;
  const dh = ih * scale;
  const dx = (gridWidth - dw) / 2;
  const dy = (gridHeight - dh) / 2;
  ctx.drawImage(img, dx, dy, dw, dh);

  return ctx.getImageData(0, 0, gridWidth, gridHeight);
}

/**
 * Grid width from image aspect and fixed row count (height = rowCount).
 *
 * @param {HTMLImageElement} img
 * @param {number} rowCount
 * @returns {{ gridWidth: number, gridHeight: number }}
 */
export function gridSizeForImage(img, rowCount) {
  const ih = img.naturalHeight;
  const iw = img.naturalWidth;
  if (ih <= 0 || iw <= 0) {
    return { gridWidth: rowCount, gridHeight: rowCount };
  }
  const aspect = iw / ih;
  const gridHeight = rowCount;
  const gridWidth = Math.max(1, Math.round(rowCount * aspect));
  return { gridWidth, gridHeight };
}

/**
 * Common grid for two images so morph masks align.
 *
 * @param {HTMLImageElement} imgA
 * @param {HTMLImageElement} imgB
 * @param {number} rowCount
 * @returns {{ gridWidth: number, gridHeight: number }}
 */
export function unifiedGridForImages(imgA, imgB, rowCount) {
  const a = gridSizeForImage(imgA, rowCount);
  const b = gridSizeForImage(imgB, rowCount);
  return {
    gridWidth: Math.max(a.gridWidth, b.gridWidth),
    gridHeight: rowCount,
  };
}

/**
 * @param {string} src
 * @returns {Promise<HTMLImageElement>}
 */
export function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}
